/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ITextFieldProps } from '@fluentui/react';
import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { IDialogProps, IDialogStyles } from '@fluentui/react/lib/Dialog';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { AnimationClassNames } from '@fluentui/react/lib/Styling';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { getStylistV2, important } from 'src/core/themes/luisStylist';
import { globalMessages } from 'src/core/utils/globalMessages';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { entitiesPageMessages } from 'src/luis/appShell/buildPane/entitiesPage/entitiesPageMessages';
import { ModelType } from 'src/luis/types/ModelType';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { RegexEntityStore } from 'src/luis/types/stores/entities/RegexEntityStore';
import { SimpleEntityStore } from 'src/luis/types/stores/entities/SimpleEntityStore';
import { CreateMLEntityScreen } from 'src/luis/verticals/createEntityModal/CreateMLEntityScreen';
import { EntityTypeSelection } from 'src/luis/verticals/createEntityModal/EntityTypeSelection';
import { EntityTypeTutorial } from 'src/luis/verticals/createEntityModal/EntityTypeTutorial';
import { EntityCreationDto, ListEntityCreationDto, RegexEntityCreationDto } from 'src/luis/verticals/createEntityModal/types';

const messages = defineMessages({
    entityNameLabel: { id: 'entities.createEntityModal.entityNameLabel', defaultMessage: 'Name' },
    regexFieldLabel: { id: 'entities.createEntityModal.regexFieldLabel', defaultMessage: 'Regex' },
    footerNextButton: { id: 'entities.createEntityModal.footerNextButton', defaultMessage: 'Next' },
    footerBackButton: { id: 'entities.createEntityModal.footerBackButton', defaultMessage: 'Back' },
    listFieldLabel: { id: 'entities.createEntityModal.listFieldLabel', defaultMessage: 'Synonyms for' },
    footerCreateButton: { id: 'entities.createEntityModal.footerCreateButton', defaultMessage: 'Create' },
    addStructureLabel: { id: 'entities.createEntityModal.addStructureLabel', defaultMessage: 'Add structure' },
    entityNamePlaceholder: { id: 'entities.createEntityModal.entityNamePlaceholder', defaultMessage: 'Name your entity' },
    defaultModalHeaderText: { id: 'entities.createEntityModal.defaultModalHeaderText', defaultMessage: 'Create an entity' },
    regexFieldPlaceholder: { id: 'entities.createEntityModal.regexFieldPlaceholder', defaultMessage: 'Enter your regex here' },
    mlStructureModalHeaderText: { id: 'entities.createEntityModal.mlStructureModalHeaderText', defaultMessage: 'Add subentities' },
    mlStructureModalHeaderOptionalText: {
        id: 'entities.createEntityModal.mlStructureModalHeaderOptionalText',
        defaultMessage: '(optional)'
    },
    listFieldPlaceholder: {
        id: 'entities.createEntityModal.listFieldPlaceholder',
        defaultMessage: 'Enter synonyms for {token} separated by commas'
    }
});

type CreateEntityModalProps = BasicModalProps<EntityStore> & {
    /**
     * Initial name for the entity being created
     */
    entityName?: string;

    /**
     * Which types of entities the user can create from this modal
     */
    allowedModelTypes: readonly ModelType[];

    /**
     * Name of the token that initiated the creation of a new list type entity
     */
    token?: string;
};

const { styleComponent, styleSpan } = getStylistV2('CreateEntityModal');

const modalStyles = classNamesFunction<IDialogProps, IDialogStyles>()({
    main: { selectors: { '& .ms-Dialog-actionsRight': { display: 'flex', flexDirection: 'row-reverse', justifyContent: 'space-between' } } }
});

const ModalHeaderMainText = styleSpan('ModalHeaderMainText', { marginRight: '6px' });
const ModalHeaderOptionalText = styleSpan('ModalHeaderOptionalText', { fontWeight: 400 });

const EntityNameTextField = styleComponent<ITextFieldProps>(LuisTextField)('EntityNameTextField', { marginBottom: '32px' });
const FooterButtonsContainer = styleComponent<IStackProps>(Stack)('FooterButtonsContainer', theme =>
    important({ marginLeft: theme.marginLeft })
);

export const CreateEntityModal = withModalMounter(
    observer((props: CreateEntityModalProps) => {
        const { entityName, allowedModelTypes, onSubmit, onDismiss, token } = props;

        const { localize } = useLocalization();
        const { dispatcher } = useGlobalState();

        const [currentScreen, setCurrentScreen] = React.useState<number>(0);
        const [isMlStructureChecked, setIsMlStructureChecked] = React.useState<boolean>(false);

        const [entityCreationDto, setEntityCreationDto] = React.useState<EntityCreationDto>({
            name: entityName || '',
            typeId: ModelType.Simple
        });

        const renameEntity = (newName: string) => setEntityCreationDto({ ...entityCreationDto, name: newName });

        const changeEntityType = (newType: ModelType) => setEntityCreationDto({ ...entityCreationDto, typeId: newType });

        const onChangeListEntityStore = (synonyms: string) => setEntityCreationDto({ ...entityCreationDto, synonyms });

        const onChangeSimpleEntityStore = (newSimpleEntityStore: SimpleEntityStore) =>
            setEntityCreationDto({ ...entityCreationDto, children: toJS(newSimpleEntityStore.children) });

        const onChangeRegexEntityStore = (newRegexPattern: string) =>
            setEntityCreationDto({ ...entityCreationDto, pattern: newRegexPattern });

        const createEntityTracker = useProgressTracker<EntityStore>({
            onComplete: entityStore => {
                onSubmit?.(entityStore);
            }
        });

        const createEntityToastData = {
            running: { title: localize(entitiesPageMessages.createEntityToastStart) },
            failed: { title: localize(entitiesPageMessages.createEntityToastFailed) },
            completed: { title: localize(entitiesPageMessages.createEntityToastSuccess) }
        };

        const createEntity = () => {
            switch (entityCreationDto.typeId) {
                case ModelType.Simple:
                    const newSimpleEntityStore = entityCreationDto as SimpleEntityStore;
                    dispatcher.dispatch('createSimpleEntityV2', {
                        tracker: createEntityTracker,
                        name: newSimpleEntityStore.name,
                        toastData: createEntityToastData,
                        children: newSimpleEntityStore.children
                    });
                    break;

                case ModelType.List:
                    const newListEntityCreationDto = entityCreationDto as ListEntityCreationDto;
                    const sublistValues = newListEntityCreationDto.synonyms ? newListEntityCreationDto.synonyms.split(',') : [];

                    const sublists = token && sublistValues.length ? [{ id: getId(), name: token, values: sublistValues }] : [];

                    dispatcher.dispatch('createListEntity', {
                        sublists,
                        tracker: createEntityTracker,
                        toastData: createEntityToastData,
                        name: newListEntityCreationDto.name
                    });
                    break;

                case ModelType.Regex:
                    const newRegexEntityStore = entityCreationDto as RegexEntityStore;
                    dispatcher.dispatch('createRegexEntity', {
                        name: entityCreationDto.name,
                        tracker: createEntityTracker,
                        toastData: createEntityToastData,
                        pattern: newRegexEntityStore.pattern
                    });
                    break;

                case ModelType.PatternAny:
                    dispatcher.dispatch('createPatternAnyEntity', {
                        name: entityCreationDto.name,
                        tracker: createEntityTracker,
                        toastData: createEntityToastData
                    });
                    break;

                default:
            }
        };

        const isNameValid = !!entityCreationDto.name;
        const isRegexValid = entityCreationDto.typeId === ModelType.Regex && (entityCreationDto as RegexEntityCreationDto).pattern?.length;
        const isFormValid = entityCreationDto.typeId === ModelType.Regex ? isNameValid && isRegexValid : isNameValid;

        const getModalTitle = (): string | JSX.Element => {
            if (currentScreen === 1 && entityCreationDto.typeId === ModelType.Simple) {
                return (
                    <>
                        <ModalHeaderMainText>{localize(messages.mlStructureModalHeaderText)}</ModalHeaderMainText>

                        <ModalHeaderOptionalText>{localize(messages.mlStructureModalHeaderOptionalText)}</ModalHeaderOptionalText>
                    </>
                );
            }
            return localize(messages.defaultModalHeaderText);
        };

        const renderContent = () => {
            if (currentScreen === 1 && entityCreationDto.typeId === ModelType.Simple) {
                return <CreateMLEntityScreen newEntityName={entityCreationDto.name} onChange={onChangeSimpleEntityStore} />;
            }
            return (
                <>
                    <EntityNameTextField
                        required
                        autoFocus
                        value={entityCreationDto.name}
                        data-automation-id="CreateEntityField"
                        label={localize(messages.entityNameLabel)}
                        onChange={(_, newName) => renameEntity(newName)}
                        placeholder={localize(messages.entityNamePlaceholder)}
                    />

                    <Stack>
                        <EntityTypeSelection
                            selectedModelType={entityCreationDto.typeId}
                            allowedModelTypes={allowedModelTypes}
                            onChangeModelType={changeEntityType}
                        />

                        <EntityTypeTutorial selectedModelType={entityCreationDto.typeId} />

                        {entityCreationDto.typeId === ModelType.Simple && (
                            <Checkbox
                                className={AnimationClassNames.fadeIn400}
                                checked={isMlStructureChecked}
                                onChange={() => setIsMlStructureChecked(!isMlStructureChecked)}
                                label={localize(messages.addStructureLabel)}
                            />
                        )}

                        {entityCreationDto.typeId === ModelType.List && token && (
                            <LuisTextField
                                label={`${localize(messages.listFieldLabel)} "${token}"`}
                                placeholder={localize(messages.listFieldPlaceholder, { token })}
                                value={(entityCreationDto as ListEntityCreationDto).synonyms || ''}
                                onChange={(_, synonyms) => onChangeListEntityStore(synonyms)}
                            />
                        )}

                        {entityCreationDto.typeId === ModelType.Regex && (
                            <LuisTextField
                                label={localize(messages.regexFieldLabel)}
                                placeholder={localize(messages.regexFieldPlaceholder)}
                                required
                                value={(entityCreationDto as RegexEntityCreationDto).pattern || ''}
                                onChange={(_, newRegexPattern) => onChangeRegexEntityStore(newRegexPattern)}
                            />
                        )}
                    </Stack>
                </>
            );
        };

        const renderFooter = () => {
            const showCreateButton = !(entityCreationDto.typeId === ModelType.Simple && isMlStructureChecked && currentScreen === 0);
            const showBackButton = currentScreen !== 0;

            const actions = [
                <FooterButtonsContainer horizontal tokens={{ childrenGap: 10 }} key="createModalFooterButtonsContainer">
                    {showCreateButton ? (
                        <PrimaryButton
                            disabled={!isFormValid}
                            onClick={createEntity}
                            key="createModalFooterCreateButton"
                            data-automation-id="ConfirmCreateEntityButton">
                            {localize(messages.footerCreateButton)}
                        </PrimaryButton>
                    ) : (
                        <PrimaryButton
                            disabled={!isFormValid}
                            onClick={() => setCurrentScreen(currentScreen + 1)}
                            key="createModalFooterNextButton">
                            {localize(messages.footerNextButton)}
                        </PrimaryButton>
                    )}

                    <DefaultButton onClick={onDismiss}>{localize(globalMessages.cancel)}</DefaultButton>
                </FooterButtonsContainer>
            ];

            if (showBackButton) {
                actions.push(
                    <DefaultButton onClick={() => setCurrentScreen(currentScreen - 1)} key="createModalFooterBackButton">
                        {localize(messages.footerBackButton)}
                    </DefaultButton>
                );
            }

            return actions;
        };

        return (
            <BasicModal
                {...props}
                minWidth={800}
                styles={modalStyles}
                onRenderFooter={renderFooter}
                disabled={createEntityTracker.isRunning}
                dialogContentProps={{ title: getModalTitle() }}>
                {renderContent()}
            </BasicModal>
        );
    })
);
